/*
 * Growth receipts from the client: the funnel steps only the browser can see,
 * sent to QLearn-service's POST /qlearn/api/growth/receipt, which logs one
 * identifier-free `growth_receipt` line per step (no id, address, name or token
 * ever leaves the browser here: the body is two or three constants).
 *
 * The endpoint's contract (QLearn-service ClientReceipt), every value a string
 * and no other key, else a 400:
 *   { event: "first_quiz_results_cta", action: "shown" | "remind_me" |
 *     "calendar" | "start_next" | "dismiss" }
 *   { event: "practice_email_click", action: <utm_content>, campaign:
 *     <utm_campaign> }   (utm_content absite_free | absite | general,
 *     utm_campaign newuser_practice_d<1-7>_<YYYYMM>)
 * It needs a signed-in session, allows 30 calls per account per hour (429
 * after that), answers 200 { accepted: true } to every valid body, and logs a
 * repeat at most once (practice email: per account per UTC day; results CTA:
 * per account per action).
 *
 * The one-tap ABSITE practice card does not use this path: its quiz create
 * carries `source: ONE_TAP_ABSITE_SOURCE` and the server logs `one_tap_start`.
 *
 * Nothing here can throw or delay the page: every send is fire-and-forget and
 * resolves to true (accepted) or false.
 */
import url from "../constants/urlList";

export const GROWTH_RECEIPT_EVENTS = Object.freeze({
  FIRST_QUIZ_RESULTS_CTA: "first_quiz_results_cta",
  PRACTICE_EMAIL_CLICK: "practice_email_click",
});

export const FIRST_QUIZ_RESULTS_CTA_ACTIONS = Object.freeze([
  "shown", "remind_me", "calendar", "start_next", "dismiss",
]);

// CreateQuizDto.source for a quiz started from the one-tap ABSITE practice
// card (QLearn-service GrowthReceipts.ONE_TAP_ABSITE_SOURCE).
export const ONE_TAP_ABSITE_SOURCE = "one_tap_absite";

// The practice email's CTA (QLearn-service PracticeEmailComposer.ctaUrl):
// utm_source=qlearn_email&utm_medium=email&utm_campaign=newuser_practice_d<N>_<YYYYMM>
// &utm_content=<absite_free|absite|general>.
const PRACTICE_EMAIL_SOURCE = "qlearn_email";
const PRACTICE_EMAIL_MEDIUM = "email";
// The server's exact shape (QLearn-service ClientReceipt): d1..d7, years 2020..2099.
const PRACTICE_EMAIL_CAMPAIGN = /^newuser_practice_d[1-7]_20[2-9]\d(0[1-9]|1[0-2])$/;
const PRACTICE_EMAIL_CONTENTS = new Set(["absite_free", "absite", "general"]);

// A landing from the email waits here until the visitor is signed in (a
// signed-out visit signs in first, often through Google, which leaves the
// site and comes back without the campaign keys).
export const PRACTICE_EMAIL_PENDING_KEY = "qlearn_practice_email_click_pending";
// A sign-in that takes longer than this is no longer "landed from the email".
export const PRACTICE_EMAIL_PENDING_MAX_AGE_MS = 60 * 60 * 1000;
// The last landing reported from this browser, as "<campaign>|<content>|<UTC day>",
// so a reload of the same CTA address that day is not captured again. The
// address itself keeps its campaign keys: GTM's tags and the sign-in redirect
// read them there (ads attribution contract), so they are never stripped.
export const PRACTICE_EMAIL_REPORTED_KEY = "qlearn_practice_email_click_reported";

const reportedMarker = (click, nowMs) =>
  `${click.campaign}|${click.content}|${new Date(nowMs).toISOString().slice(0, 10)}`;

export const postGrowthReceipt = (body, fetchImpl = typeof fetch === "function" ? fetch : undefined) => {
  try {
    if (!fetchImpl) return Promise.resolve(false);
    return Promise.resolve(fetchImpl(url.growthReceipt, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }))
      .then((response) => !!response && response.status === 200)
      .catch(() => false);
  } catch {
    // A receipt is measurement only: a fetch that throws synchronously reads as not accepted.
    return Promise.resolve(false);
  }
};

// For the first-quiz results screen: sendGrowthReceipt("first_quiz_results_cta", "remind_me").
// An event or action outside the contract is not sent at all.
export const sendGrowthReceipt = (event, action, fetchImpl) => {
  if (event !== GROWTH_RECEIPT_EVENTS.FIRST_QUIZ_RESULTS_CTA
    || !FIRST_QUIZ_RESULTS_CTA_ACTIONS.includes(action)) {
    return Promise.resolve(false);
  }
  return postGrowthReceipt({ event, action }, fetchImpl);
};

// { campaign, content } when an address is the practice email's CTA, else undefined.
export const practiceEmailClickFromSearch = (search) => {
  let params;
  try {
    params = new URLSearchParams(search || "");
  } catch {
    // An address that cannot be parsed is not the practice email's CTA.
    return undefined;
  }
  const campaign = params.get("utm_campaign");
  const content = params.get("utm_content");
  if (params.get("utm_source") !== PRACTICE_EMAIL_SOURCE
    || params.get("utm_medium") !== PRACTICE_EMAIL_MEDIUM
    || typeof campaign !== "string" || !PRACTICE_EMAIL_CAMPAIGN.test(campaign)
    || !PRACTICE_EMAIL_CONTENTS.has(content)) {
    return undefined;
  }
  return { campaign, content };
};

// Reading window.localStorage can itself throw (blocked site data), so the
// defaults are guarded too.
const defaultStorage = () => {
  try {
    return typeof window !== "undefined" ? window.localStorage : undefined;
  } catch {
    // Blocked site data: no storage, so no landing is remembered.
    return undefined;
  }
};
const defaultSearch = () => {
  try {
    return typeof window !== "undefined" ? window.location?.search : "";
  } catch {
    // No readable address: nothing to capture.
    return "";
  }
};

// The address is read once per page load (the app's load effect or the first
// lookup, whichever runs first), so later lookups on the same page never
// re-capture, and re-send, the landing.
let landingChecked = false;

export const resetPracticeEmailLandingCheck = () => {
  landingChecked = false;
};

// Once per page load: remember a landing from the practice email. Never throws.
export const capturePracticeEmailLanding = ({
  search = defaultSearch(),
  storage = defaultStorage(),
  now = Date.now,
} = {}) => {
  try {
    if (landingChecked) return false;
    landingChecked = true;
    const click = practiceEmailClickFromSearch(search);
    if (!click || !storage) return false;
    const at = now();
    if (storage.getItem(PRACTICE_EMAIL_REPORTED_KEY) === reportedMarker(click, at)) return false;
    storage.setItem(PRACTICE_EMAIL_PENDING_KEY, JSON.stringify({ ...click, at }));
    return true;
  } catch {
    // Storage refused the write: the landing is not remembered, the page is unaffected.
    return false;
  }
};

// After a successful signed-in lookup (GET /user/me): report a fresh pending
// landing, once (the pending entry is removed before sending, so concurrent
// lookups never repeat it). Never throws.
export const reportPendingPracticeEmailClick = ({
  search = defaultSearch(),
  storage = defaultStorage(),
  now = Date.now,
  send = postGrowthReceipt,
} = {}) => {
  try {
    if (!storage) return Promise.resolve(false);
    // The address itself may still be the landing (an already signed-in visit
    // whose lookup answered before the app's own load effect ran).
    capturePracticeEmailLanding({ search, storage, now });
    const raw = storage.getItem(PRACTICE_EMAIL_PENDING_KEY);
    if (!raw) return Promise.resolve(false);
    storage.removeItem(PRACTICE_EMAIL_PENDING_KEY);
    const pending = JSON.parse(raw);
    const age = now() - Number(pending?.at);
    if (!(age >= 0 && age <= PRACTICE_EMAIL_PENDING_MAX_AGE_MS)) return Promise.resolve(false);
    const click = practiceEmailClickFromSearch(
      `?utm_source=${PRACTICE_EMAIL_SOURCE}&utm_medium=${PRACTICE_EMAIL_MEDIUM}`
      + `&utm_campaign=${encodeURIComponent(pending?.campaign ?? "")}`
      + `&utm_content=${encodeURIComponent(pending?.content ?? "")}`);
    if (!click) return Promise.resolve(false);
    storage.setItem(PRACTICE_EMAIL_REPORTED_KEY, reportedMarker(click, now()));
    return send({
      event: GROWTH_RECEIPT_EVENTS.PRACTICE_EMAIL_CLICK,
      action: click.content,
      campaign: click.campaign,
    });
  } catch {
    // Unreadable or malformed pending entry (already removed above): nothing is sent.
    return Promise.resolve(false);
  }
};
